import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import axios from "axios";
import {
  CATALOGO_CLINICO_V2_ENDPOINT,
  buildCatalogSearchParamsV2,
  createEmptyDiagnosisV2,
  normalizeCatalogResponseV2,
} from "../saludClinicaV2Utils";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DELAY_MS = 350;

const DiagnosticoSelectorV2 = ({
  value = createEmptyDiagnosisV2(),
  onChange,
  disabled = false,
  error = "",
  excludedConceptIds = [],
}) => {
  const [query, setQuery] = useState(value.nombre || value.textoOriginal || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  useEffect(() => {
    setQuery(value.nombre || value.textoOriginal || "");
  }, [value.idConcepto, value.nombre, value.pendienteRevision, value.textoOriginal]);

  useEffect(() => {
    const search = query.trim();

    if (disabled || value.pendienteRevision || search.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      setLoading(false);
      setSearchError("");
      return undefined;
    }

    if (search.length > 100) {
      setOptions([]);
      setLoading(false);
      setSearchError("La busqueda admite un maximo de 100 caracteres.");
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setLoading(true);
      setSearchError("");

      try {
        const response = await axios.get(CATALOGO_CLINICO_V2_ENDPOINT, {
          params: buildCatalogSearchParamsV2(search),
          signal: controller.signal,
        });
        setOptions(normalizeCatalogResponseV2(response.data));
      } catch (requestError) {
        if (
          requestError?.code !== "ERR_CANCELED" &&
          requestError?.name !== "CanceledError" &&
          requestError?.name !== "AbortError"
        ) {
          setOptions([]);
          setSearchError("No se pudo consultar el catálogo clínico.");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, SEARCH_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [disabled, query, value.pendienteRevision]);

  const duplicateIds = useMemo(
    () => new Set(excludedConceptIds.filter(Boolean).map(String)),
    [excludedConceptIds]
  );

  const selectDiagnosis = (option) => {
    if (!option) {
      onChange(createEmptyDiagnosisV2());
      return;
    }

    if (duplicateIds.has(String(option.idConcepto))) return;

    onChange({
      ...option,
      textoOriginal: query.trim() || option.nombre,
      pendienteRevision: false,
    });
  };

  const markPendingReview = () => {
    const originalText = query.trim();
    if (originalText.length < MIN_SEARCH_LENGTH) return;

    onChange({
      ...createEmptyDiagnosisV2(),
      nombre: originalText,
      textoOriginal: originalText,
      pendienteRevision: true,
    });
  };

  return (
    <Box>
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={8}>
          <Autocomplete
            fullWidth
            disabled={disabled}
            value={value.idConcepto ? value : null}
            inputValue={query}
            options={options}
            loading={loading}
            filterOptions={(items) => items}
            isOptionEqualToValue={(option, selected) =>
              String(option.idConcepto) === String(selected.idConcepto)
            }
            getOptionDisabled={(option) =>
              option.seleccionable === false ||
              duplicateIds.has(String(option.idConcepto))
            }
            getOptionLabel={(option) => option?.nombre || ""}
            onChange={(event, option) => selectDiagnosis(option)}
            onInputChange={(event, nextQuery, reason) => {
              setQuery(nextQuery);
              if (reason === "input") {
                onChange({
                  ...createEmptyDiagnosisV2(),
                  nombre: nextQuery,
                  textoOriginal: nextQuery,
                });
              }
            }}
            noOptionsText={
              query.trim().length < MIN_SEARCH_LENGTH
                ? "Escriba al menos dos caracteres"
                : "No se encontraron conceptos"
            }
            renderOption={(props, option) => (
              <li {...props} key={option.idConcepto}>
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {option.nombre}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[option.sistema, option.codigo].filter(Boolean).join(" · ") || "Sin código visible"}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={value.pendienteRevision ? "Diagnóstico pendiente de revisión" : "Buscar diagnóstico"}
                error={Boolean(error || searchError)}
                helperText={
                  error ||
                  searchError ||
                  (value.pendienteRevision
                    ? "Se enviará el texto original para revisión clínica."
                    : "Seleccione un concepto del catálogo.")
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                inputProps={{ ...params.inputProps, maxLength: 100 }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Código clínico"
            value={value.pendienteRevision ? "Pendiente" : value.codigo || ""}
            InputProps={{ readOnly: true }}
            helperText={value.pendienteRevision ? "Sin código asignado" : value.sistema || " "}
          />
        </Grid>
      </Grid>
      {!value.idConcepto && !value.pendienteRevision && (
        <Button
          type="button"
          size="small"
          startIcon={<SearchOffOutlinedIcon />}
          onClick={markPendingReview}
          disabled={disabled || query.trim().length < MIN_SEARCH_LENGTH}
          sx={{ mt: 1 }}
        >
          No encuentro el diagnóstico
        </Button>
      )}
    </Box>
  );
};

export default DiagnosticoSelectorV2;
